import React, { useEffect, useRef } from 'react';
import { Redirect, Switch, useHistory } from 'react-router-dom';
import { useSnackbar } from 'material-ui-snackbar-provider';

import { getAllRolesThunk, saveMyOrganization, saveUser, setAuthChecked } from './store/user/userSlice';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { getUserOrganization } from './api/organizationsApi';
import ManagementRoutes from './hoc/ManagementRoutes';
import SuperAdminRoutes from './hoc/SuperAdminRoutes';
import LandingPage from './components/LandingPage';
import ProtectedRoute from './hoc/ProtectedRoute';
import { PATHS } from './enums/PATHS';
import { ROLES } from './enums/ROLES';
import { getMyUserInfo } from './api';
import { checkContentAvailability } from './api/accessesApi';
import {
  bootstrapComposerWebPreferences,
  findSearchValue,
  getPreviewAccessToken,
  getTokenFromLocalStorage,
} from './helpers';
import { updateShowWarningModal } from './store/sharedStore/sharedStoreSlice';
import AppLoader from './components/modules/FullPageLoader/AppLoader';

const AUTHENTICATED_HOME_PATHS: Record<ROLES, string> = {
  [ROLES.SUPER_ADMIN]: PATHS.ADMIN_PAGE.path,
  [ROLES.ORGANIZATION_ADMIN]: PATHS.MY_FOLDERS.path,
  [ROLES.ANALYST]: PATHS.MY_FOLDERS.path,
  [ROLES.VIEWER]: PATHS.SHARED_WITH_ME.path,
};

const MainRouter = () => {
  const user = useAppSelector((state) => state.user.role);
  const currentUser = useAppSelector((state) => state.user.currentUser);
  const authChecked = useAppSelector((state) => state.user.authChecked);
  const dispatch = useAppDispatch();
  const history = useHistory();

  const useAuth = async () => {
    try {
      const userJson = await getMyUserInfo();
      await bootstrapComposerWebPreferences();
      dispatch(saveUser(userJson.data));
    } catch {
      dispatch(setAuthChecked(true));
      if (window.location.pathname === '/') {
        return;
      }
      history.push('/');
    }
  };

  useEffect(() => {
    useAuth();
    // Prevent spacebar from scrolling down the page, which is the default behavior
    // we need spacebar for video playback
    window.addEventListener('keydown', (e) => {
      if (
        e.keyCode === 32 &&
        // @ts-ignore
        e.target.type !== 'text' &&
        // @ts-ignore
        e.target.type !== 'textarea'
      ) {
        e.preventDefault();
      }
    });
  }, []);

  const getMyOrganization = async () => {
    const res = await getUserOrganization();
    dispatch(saveMyOrganization(res.data));
  };

  useEffect(() => {
    if (user && user !== ROLES.SUPER_ADMIN) {
      getMyOrganization();
      dispatch(getAllRolesThunk());
    }
    if (user && (user === ROLES.ANALYST || user === ROLES.VIEWER) && history && history?.location?.pathname === PATHS.USERS_LIST.path) {
      history.replace(PATHS.MY_REFLEX_COMPOSERS.path);
    }

    if (user && user !== ROLES.SUPER_ADMIN && history?.location?.pathname === PATHS.SA_USERS_LIST.path) {
      if (user === ROLES.ORGANIZATION_ADMIN) {
        history.push(PATHS.USERS_LIST.path);
      } else {
        history.push(PATHS.GROUPS_MANAGEMENT.path);
      }
    }
  }, [user]);

  const emailFlag = findSearchValue('emailFlag');
  const feedId = findSearchValue('feedId');
  const folderId = findSearchValue('folderId');
  const runCheck = async (type: 'folder' | 'videofeed', id: string) => {
    try {
      const res = await checkContentAvailability(type, id);
      if (res.data?.deleted) {
        dispatch(
          updateShowWarningModal({ show: true, warningText: 'The content you are trying to access has been deleted', redirect: true }),
        );
      } else if (res.data?.noAccess) {
        dispatch(
          updateShowWarningModal({
            show: true,
            warningText: 'You do not have permission to access the content you are trying to view.',
            redirect: true,
          }),
        );
      } else if (res?.data?.differentOrg) {
        dispatch(
          updateShowWarningModal({
            show: true,
            warningText: `Your current organisation is different from the content's organisation.${
              res?.data?.organizationName ? ` The content is from ${res.data.organizationName}.` : ''
            }`,
            redirect: true,
          }),
        );
      }
    } catch (err) {
      console.error(err);
    }
  };
  useEffect(() => {
    if (emailFlag !== 'true' || !user) return;
    if (feedId) {
      runCheck('videofeed', feedId);
    } else if (folderId) {
      runCheck('folder', folderId);
    }
  }, [user, emailFlag]);

  if (!authChecked) {
    return <AppLoader />;
  }

  const authSearchParams = new URLSearchParams(window.location.search);
  const hasAuthModalQuery = authSearchParams.get('register') === 'true' || authSearchParams.get('restore') === 'true';
  const isLoggedIn = Boolean(currentUser && getTokenFromLocalStorage() && !getPreviewAccessToken());
  const authenticatedHomePath =
    isLoggedIn && user && !hasAuthModalQuery ? AUTHENTICATED_HOME_PATHS[user] : null;

  return (
    <Switch>
      <ProtectedRoute
        path={PATHS.FILE_MANAGEMENT.path}
        exact={PATHS.FILE_MANAGEMENT.exact}
        roles={[ROLES.ORGANIZATION_ADMIN, ROLES.ANALYST, ROLES.VIEWER]}
        component={ManagementRoutes}
      />
      {/* super admin routes */}
      <ProtectedRoute
        path={PATHS.ADMIN_PAGE.path}
        exact={PATHS.ADMIN_PAGE.exact}
        roles={[ROLES.SUPER_ADMIN]}
        component={SuperAdminRoutes}
      />
      {/* landing */}
      {authenticatedHomePath && <Redirect exact from={PATHS.MAIN.path} to={authenticatedHomePath} />}
      <ProtectedRoute exact={PATHS.MAIN.exact} component={LandingPage} path={PATHS.MAIN.path} />
    </Switch>
  );
};

export default MainRouter;
